import * as React from "react";
import { styled } from "@mui/material";
import { useRef, useState } from "react";
import clsx from "clsx";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { SummaryActionIcon } from "./summaryActionIcon";
import { CommonColors } from "../colors/commonColors";
import { onRtl } from "../rtl/rtlStyles";
import { copyToClipboard } from "fe-common/utils/copyToClipboard";
import { KAHUN_PLATFORM } from "@kahunmedical/user-management";
import { SectionAction } from "./types/SectionAction";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";

//region [[ Styles ]]

const SectionAreaWithCopyView = styled("div")({
  padding: "0",
  position: "relative",
  "&:not(.no-divider) .divider": {
    borderBottom: `1px solid ${CommonColors.PrimaryLight}`,
    width: "100%",
    height: "1px",
    margin: "13px 0",
  },
});

const ActionButtonsContainer = styled("div")({
  position: "absolute",
  right: 0,
  top: 0,
  ...onRtl({
    right: "auto",
    left: 0,
  }),
});
//region [[ Props ]]

export interface SectionAreaWithActionsProps {
  children: React.ReactNode;
  className?: string;
  noDivider?: boolean;
  onCopy: (containerRef: HTMLDivElement) => void;
  clinicName: string;
  clinicId: string;
  platform: KAHUN_PLATFORM;
  sectionName?: string;
  actions?: SectionAction[];
  inExtension?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SectionAreaWithActions = ({
  clinicName,
  clinicId,
  platform,
  ...props
}: SectionAreaWithActionsProps) => {
  const [showActionButtons, setShowActionButtons] = useState(false);
  const containerRef = useRef<HTMLDivElement>();
  const { translate } = useTranslateByGender();
  const handleActionClicked = (action: SectionAction, e: MouseEvent) => {
    evt({
      category: CATEGORY.PATIENT_CARD,
      action: ACTION.SECTION_ACTION_CLICKED,
      props: {
        clinicName,
        clinicId,
        platform,
        action: action.text,
        section: props.sectionName,
      },
    });
    action?.onClick?.(e, props.sectionName);
  };

  const handleCopyToClipboard = () => {
    evt({
      category: CATEGORY.PATIENT_CARD,
      action: ACTION.COPY_EXPORT_DATA_CLICKED,
      props: {
        clinicName,
        clinicId,
        platform,
      },
    });

    copyToClipboard(containerRef.current);

    props.onCopy?.(containerRef.current);
  };

  return (
    <SectionAreaWithCopyView
      ref={containerRef}
      className={clsx(
        "section-area-with-copy",
        props.className,
        showActionButtons ? "highlight" : "",
        props.noDivider ? "no-divider" : "",
      )}
      onMouseEnter={() => setShowActionButtons(true)}
      onMouseLeave={() => setShowActionButtons(false)}
    >
      {showActionButtons && (
        <ActionButtonsContainer>
          <SummaryActionIcon
            inExtension={props.inExtension}
            onClick={handleCopyToClipboard}
            noBackground
            title={translate("COPY")}
          />
          {props.actions?.map?.((action, idx) => (
            <SummaryActionIcon
              key={action.text}
              onClick={(e) => handleActionClicked(action, e)}
              title={action.text}
              icon={action.icon}
              inExtension={props.inExtension}
              noBackground
            />
          ))}
        </ActionButtonsContainer>
      )}
      {props.children}
      <div className={"divider"}>&nbsp;</div>
    </SectionAreaWithCopyView>
  );
};
